import { getArticulosStockPorDeposito } from "@/features/inventario/articulos/api/get-articulos-stock-por-deposito";

export interface FaltanteStock {
    articuloId: string;
    nombre?: string;
    disponible: number;
    requerido: number;
}

export type ItemStock = {
    articuloId: string;
    articuloNombre?: string;
    cantidad: number;
    stockDisponible?: number;
};

// Agrupa la cantidad requerida por artículo (los ítems duplicados se acumulan).
function agruparRequeridos(items: ItemStock[]): Map<string, { nombre?: string; cantidad: number }> {
    const requeridoPorArticulo = new Map<string, { nombre?: string; cantidad: number }>();
    for (const item of items) {
        const prev = requeridoPorArticulo.get(item.articuloId);
        requeridoPorArticulo.set(item.articuloId, {
            nombre: item.articuloNombre,
            cantidad: (prev?.cantidad ?? 0) + item.cantidad,
        });
    }
    return requeridoPorArticulo;
}

// Consulta en una sola llamada el saldo de todos los artículos en el depósito indicado (ignora errores de consulta).
export async function getSaldosDeposito(articuloIds: string[], depositoId: string): Promise<Map<string, number>> {
    if (articuloIds.length === 0) return new Map();
    try {
        const saldos = await getArticulosStockPorDeposito(articuloIds, depositoId);
        return new Map(Object.entries(saldos));
    } catch {
        return new Map();
    }
}

// Compara la cantidad requerida por artículo contra los saldos y devuelve los faltantes.
export function calcularFaltantes(items: ItemStock[], saldos: Map<string, number>): FaltanteStock[] {
    const faltantes: FaltanteStock[] = [];
    for (const [articuloId, req] of agruparRequeridos(items)) {
        const saldo = saldos.get(articuloId);
        if (saldo == null) continue;
        if (req.cantidad > saldo) {
            faltantes.push({ articuloId, nombre: req.nombre, disponible: saldo, requerido: req.cantidad });
        }
    }
    return faltantes;
}

// Valida stock consultando la API. Devuelve solo los ítems con stock insuficiente (vacío = todo OK).
export async function validarStockItems(items: ItemStock[], depositoId: string): Promise<FaltanteStock[]> {
    const saldos = await getSaldosDeposito([...new Set(items.map(i => i.articuloId))], depositoId);
    return calcularFaltantes(items, saldos);
}

// Valida stock localmente usando el stockDisponible guardado en cada ítem (sin API).
// Los ítems de artículos sin stockDisponible conocido se devuelven en sinDato para validarlos aparte.
export function validarStockLocal(items: ItemStock[]): { faltantes: FaltanteStock[]; sinDato: ItemStock[] } {
    const saldos = new Map<string, number>();
    const articulosSinDato = new Set<string>();

    for (const item of items) {
        if (item.stockDisponible == null) articulosSinDato.add(item.articuloId);
        // El último ítem agregado tiene el valor más fresco.
        else saldos.set(item.articuloId, item.stockDisponible);
    }

    const conDato = items.filter(i => !articulosSinDato.has(i.articuloId));
    const sinDato = items.filter(i => articulosSinDato.has(i.articuloId));

    return { faltantes: calcularFaltantes(conDato, saldos), sinDato };
}
